import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  effect,
  inject,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountPhotoComponent } from '../../components/account-photo/account-photo.component';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { AccountService, ActivityTypeEnum } from '@olv/ui-shared/services/account.service';
import { MerchantAccountService } from '@olv/ui-shared/services/merchant-account.service';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import {
  Account,
  ActivityLogItem,
  AccountStatus,
  UpdateAccountDto,
  Group,
} from '@olv/ui-shared/models/account.interface';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { PillDropdownComponent } from '@olv/ui-shared/ui-shared/pill-dropdown/pill-dropdown.component';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { ShowMoreComponent } from '@olv/ui-shared/ui-shared/show-more/show-more.component';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { EmptyStateComponent } from '@olv/ui-shared/ui-shared/empty-state/empty-state.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import { TabStripDirective } from '@olv/ui-shared/directives/tab-strip.directive';

function enumToFilterFields(enumObj: any): { label: string; value: ActivityTypeEnum }[] {
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      label: key.replace(/([a-z])([A-Z])/g, '$1 $2'),
      value: enumObj[key] as ActivityTypeEnum,
    }));
}
@Component({
  selector: 'app-account-detail',
  standalone: true,
  imports: [
    TabStripDirective,
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    TextInputComponent,
    PillDropdownComponent,
    HasPermissionDirective,
    ShowMoreComponent,
    LoadingSpinnerComponent,
    MatIconModule,
    EmptyStateComponent,
    ButtonComponent,
  ],
  templateUrl: './account-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./account-detail.component.scss'],
})
export class AccountDetailComponent implements OnInit {
  @Input() userId = '';
  @Input() merchantId = '';
  @Input() isMerchant = false;
  @Input() return = '';
  @Output() closed = new EventEmitter<void>();

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private accountService = inject(AccountService);
  private merchantAccountService = inject(MerchantAccountService);
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private readonly toastr = inject(ToastService);
  private formErrorService = inject(FormErrorService);
  private readonly appCtx = inject(AppContextService);
  protected readonly formatDateStandard = formatDateStandard;
  private dialog = inject(MatDialog);

  // Component state
  account: Account | null = null;
  activityLog: ActivityLogItem[] = [];
  activeTab: 'activity' | 'edit' = 'activity';
  loading = false;
  editForm!: FormGroup;
  securityDropdownOpen = false;
  photoPreviewUrl: string | null = null;
  photoFile: File | null = null;
  showConfirm = false;
  confirmMessage = '';
  pendingAction = '';
  activityPage = 0;
  activityPageSize = 10;
  activityLoading = false;
  hasMoreActivities = false;
  isMoreActivityLoading = false;
  totalActivityCount = 0;
  entityLabel!: 'User' | 'Account';
  filtersApplied = false;

  // Constants
  readonly AccountStatus = AccountStatus;
  roleOptions: { label: string; value: string }[] = [];
  openDropdown: string | null = null;

  selectedActivityOption = 'Activity Type';
  selectedActivityType: ActivityTypeEnum | undefined = undefined;
  selectedTimeOption = 'All Time';

  readonly activityFilterOptions: {
    label: string;
    value: ActivityTypeEnum | undefined;
  }[] = [...enumToFilterFields(ActivityTypeEnum)];

  readonly timeFilterOptions = [
    { label: 'All Time', value: '' },
    { label: 'Last 24 Hours', value: '24h' },
    { label: 'Last Week', value: '7d' },
    { label: 'Last Month', value: '30d' },
  ];
  private destroy$ = new Subject<void>();
  readonly isCurrentUser = computed(() => {
    const profile = this.accountService.accountProfile();
    const account = this.account;

    if (!profile || !account) {
      return false;
    }

    return profile.id === account.id;
  });

  constructor() {
    // React to profile updates by reloading the account
    effect(() => {
      const accountUpdated = this.accountService.profileUpdated();
      const merchantUpdated = this.merchantAccountService.profileUpdated();
      if ((accountUpdated || merchantUpdated) && this.account?.id) {
        this.loadAccount(this.account.id);
      }
    });
  }

  ngOnInit(): void {
    const appName = this.appCtx.getAppName();
    if ((this.return === 'merchant' && appName === 'settlement') || appName === 'merchant') {
      this.entityLabel = 'User';
    } else {
      this.entityLabel = 'Account';
    }

    this.initializeEditForm();
    this.updateEditMode();
    const accountId = this.userId ? this.userId : this.route.snapshot.paramMap.get('id');
    if (accountId) {
      this.loadGroupsThenAccount(accountId);
      this.loadActivityLog(accountId);
    }
  }

  private loadGroupsThenAccount(accountId: string): void {
    this.loading = true;
    const merchantId = this.route.snapshot.paramMap.get('id');

    const groups$ =
      this.return === 'merchant' && merchantId
        ? this.merchantAccountService.getAccountGroups(merchantId)
        : this.accountService.getAccountGroups();

    groups$.subscribe({
      next: (groups) => {
        this.roleOptions = groups.map((group) => ({
          label: group.name,
          value: group.id,
        }));
        this.loadAccount(accountId);
      },
      error: () => {
        this.loadAccount(accountId);
      },
    });
  }

  private loadAccount(id: string): void {
    this.loading = true;
    const merchantId = this.route.snapshot.paramMap.get('id');

    const service$ =
      this.return === 'merchant' && merchantId
        ? this.merchantAccountService.getAccount(merchantId, id)
        : this.accountService.getAccount(id);

    service$.subscribe({
      next: (account) => {
        this.account = account;
        if (this.return === 'merchant' && merchantId) {
          this.merchantAccountService.selectAccount(account);
        } else {
          this.accountService.selectAccount(account);
        }
        this.populateEditForm();
        this.updateEditMode();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
  private loadActivityLog(
    accountId: string,
    activityType?: ActivityTypeEnum,
    startDateTime?: string,
    endDateTime?: string,
    loadMore = false,
  ): void {
    if (!loadMore) {
      this.activityPage = 0;
      this.activityLog = [];
      this.activityLoading = true;
    } else {
      this.isMoreActivityLoading = true;
    }

    const skip = this.activityPage * this.activityPageSize;

    this.accountService
      .getActivityLog(
        accountId,
        activityType,
        startDateTime,
        endDateTime,
        skip,
        this.activityPageSize,
        this.merchantId,
        this.isMerchant,
      )
      .subscribe({
        next: (response) => {
          const { items, totalCount } = response;

          this.activityLog = loadMore ? [...this.activityLog, ...items] : items;

          this.totalActivityCount = totalCount;
          this.hasMoreActivities = this.activityLog.length < totalCount;

          this.activityLoading = false;
          this.isMoreActivityLoading = false;
        },
        error: () => {
          this.hasMoreActivities = false;
          this.activityLoading = false;
          this.isMoreActivityLoading = false;
        },
      });
  }

  loadMoreActivity(): void {
    if (!this.account?.id || this.isMoreActivityLoading || !this.hasMoreActivities) return;
    this.activityPage++;

    const activityType = this.selectedActivityType || undefined;
    const { fromDate, toDate } = this.getDateRange(
      this.timeFilterOptions.find((o) => o.label === this.selectedTimeOption)?.value || '',
    );

    this.loadActivityLog(this.account.id, activityType, fromDate, toDate, true);
  }
  private initializeEditForm(): void {
    this.editForm = this.fb.group({
      firstName: ['', [Validators.required, ValidationService.textValidator(2, 100, "^[\\p{L}][\\p{L} .'’-]*$")]],
      lastName: ['', [Validators.required, ValidationService.textValidator(2, 100, "^[\\p{L}][\\p{L} .'’-]*$")]],
      position: ['', [Validators.required, ValidationService.textValidator(2, 100, "^[\\p{L}\\p{N} .,'&/-]*$")]],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      role: [[], [Validators.required]],
    });
  }

  getFieldError(controlName: string, fieldName: string, customMessages?: { [key: string]: string }): string {
    return this.formErrorService.getErrorMessage(this.editForm.get(controlName), fieldName, customMessages);
  }

  private populateEditForm(): void {
    if (this.account && this.editForm) {
      this.editForm.patchValue({
        firstName: this.account.firstName,
        lastName: this.account.lastName,
        position: this.account.position,
        email: this.account.email,
        role:
          this.account.groups
            ?.map((group) => {
              const matchedRole = this.roleOptions.find((opt) => opt.label === group.name);
              return matchedRole?.value;
            })
            .filter(Boolean) || [],
      });
    }
  }

  private updateEditMode(): void {
    if (!this.editForm) return;
    const canEdit = this.auth.hasPermission(this.permissionResource, Scopes.Update);
    if (canEdit) {
      this.editForm.enable({ emitEvent: false });
      this.editForm.get('email')?.disable({ emitEvent: false });
    } else {
      this.editForm.disable({ emitEvent: false });
    }
  }

  onBack(): void {
    if (this.return === 'merchant') {
      this.closed.emit();
    } else {
      this.router.navigate(['/account-management']);
    }
  }

  setActiveTab(tab: 'activity' | 'edit'): void {
    this.activeTab = tab;
    if (tab === 'edit') {
      this.populateEditForm();
    }
  }

  onSaveChanges(): void {
    if (this.loading) return;

    if (this.editForm.invalid || !this.account) {
      this.markFormGroupTouched();
      return;
    }

    this.loading = true;
    const formValue = this.editForm.value;

    const updateData: UpdateAccountDto = {};
    const accountId = this.account.id!;

    // Compare and add 'name' if changed
    if (formValue.firstName !== this.account.firstName) {
      updateData.firstName = formValue.firstName;
    }
    if (formValue.lastName !== this.account.lastName) {
      updateData.lastName = formValue.lastName;
    }

    // Compare and add 'position' if changed
    if (formValue.position !== this.account.position) {
      updateData.position = formValue.position;
    }
    if (this.photoFile) {
      updateData.profileFile = this.photoFile;
    }
    // Compare and add 'groups' if changed
    const originalGroupIds = this.account.groups
      ?.map((g) => {
        const match = this.roleOptions.find((r) => r.label === g.name);
        return match?.value;
      })
      .filter(Boolean)
      .sort();

    const currentGroupIds = [...(formValue.role || [])].sort();

    if (JSON.stringify(originalGroupIds) !== JSON.stringify(currentGroupIds)) {
      updateData.groups = currentGroupIds;
    }

    const merchantId = this.route.snapshot.paramMap.get('id');

    const service$ =
      this.return === 'merchant' && merchantId
        ? this.merchantAccountService.updateAccount(merchantId, accountId, updateData)
        : this.accountService.updateAccount(accountId, updateData);

    service$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.photoFile = null;
          this.photoPreviewUrl = null;
          this.toastr.success('Account updated successfully');
        },
      });
  }

  onCancelEdit(): void {
    this.populateEditForm();
    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
  }

  onDeactivateAccount(): void {
    if (this.loading || !this.account || this.isCurrentUser()) return;

    const action = this.account.status === AccountStatus.ACTIVE ? 'deactivate' : 'activate';
    const confirmMessage = `Are you sure you want to ${action} this account?`;

    if (confirm(confirmMessage)) {
      this.loading = true;
      const merchantId = this.route.snapshot.paramMap.get('id');

      let serviceCall: Observable<void>;

      if (this.return === 'merchant' && merchantId) {
        serviceCall =
          this.account.status === AccountStatus.ACTIVE
            ? this.merchantAccountService.deactivateAccount(merchantId, this.account.id)
            : this.merchantAccountService.activateAccount(merchantId, this.account.id);
      } else {
        serviceCall =
          this.account.status === AccountStatus.ACTIVE
            ? this.accountService.deactivateAccount(this.account.id)
            : this.accountService.activateAccount(this.account.id);
      }

      serviceCall.subscribe({
        next: () => {
          // Update local account status
          if (this.account) {
            this.account.status =
              this.account.status === AccountStatus.ACTIVE ? AccountStatus.INACTIVE : AccountStatus.ACTIVE;
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    }
  }

  toggleSecurityDropdown(): void {
    this.securityDropdownOpen = !this.securityDropdownOpen;
  }

  onSecurityAction(action: string): void {
    if (!this.account) return;

    this.pendingAction = action;
    switch (action) {
      case 'reset-password':
        this.confirmMessage = "Are you sure you want to reset this user's password?";
        break;
      case 'reset-2fa':
        this.confirmMessage = "Are you sure you want to reset this user's 2FA settings?";
        break;
      case 'reset-account':
        this.confirmMessage = 'Are you sure you want to reset this account? This action cannot be undone.';
        break;
    }
    this.showConfirm = true;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.editForm.controls).forEach((key) => {
      const control = this.editForm.get(key);
      control?.markAsTouched();
    });
  }

  get statusClass(): string {
    return this.account?.status === AccountStatus.ACTIVE ? 'active' : 'inactive';
  }

  get deactivateButtonState(): 'disabled' | 'default' {
    return this.loading || this.isCurrentUser() ? 'disabled' : 'default';
  }

  get cancelEditButtonState(): 'disabled' | 'default' {
    return this.loading || this.isUnchanged() ? 'disabled' : 'default';
  }

  get submitEditButtonState(): 'disabled' | 'default' {
    return this.loading || this.isUnchanged() ? 'disabled' : 'default';
  }

  isUnchanged(): boolean {
    if (!this.account || !this.editForm) return true;

    const current = this.editForm.value;

    const originalRoleIds = this.account.groups
      ?.map((group) => {
        const matched = this.roleOptions.find((opt) => opt.label === group.name);
        return matched?.value;
      })
      .filter(Boolean)
      .sort();

    const currentRoleIds = [...(current.role || [])].sort();

    return (
      current.firstName === this.account.firstName &&
      current.lastName === this.account.lastName &&
      current.position === this.account.position &&
      JSON.stringify(currentRoleIds) === JSON.stringify(originalRoleIds)
    );
  }

  get groupNames(): string {
    return this.account?.groups?.map((g: Group) => g.name).join(' / ') || '';
  }

  get activityFilterLabels(): string[] {
    return this.activityFilterOptions.map((o) => o.label);
  }
  get timeFilterLabels(): string[] {
    return this.timeFilterOptions.map((o) => o.label);
  }
  private getDateRange(range: string): { fromDate?: string; toDate?: string } {
    const now = new Date();
    let from: Date;

    switch (range) {
      case '24h':
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        return {};
    }

    return {
      fromDate: from.toISOString(),
      toDate: now.toISOString(),
    };
  }

  onOptionSelected(event: { id: string; value: string }): void {
    this.activityPage = 0;

    if (event.id === 'activity') {
      const option = this.activityFilterOptions.find((o) => o.label === event.value);
      if (option) {
        this.selectedActivityOption = option.label;
        this.selectedActivityType = option.value;
      } else {
        this.selectedActivityOption = 'Activity Type';
        this.selectedActivityType = undefined;
      }
    }

    if (event.id === 'time') {
      const timeOption = this.timeFilterOptions.find((o) => o.label === event.value);
      if (timeOption) {
        this.selectedTimeOption = timeOption.label;
      }
    }

    this.openDropdown = null;
    this.filtersApplied = this.selectedActivityType !== undefined || this.selectedTimeOption !== 'All Time';

    if (this.account?.id) {
      const activityType = this.selectedActivityType;
      const { fromDate, toDate } = this.getDateRange(
        this.timeFilterOptions.find((o) => o.label === this.selectedTimeOption)?.value || '',
      );
      this.loadActivityLog(this.account.id, activityType, fromDate, toDate);
    }
  }
  openPhotoEditor(): void {
    const dialogRef = this.dialog.open(AccountPhotoComponent, {
      width: '600px',
      maxHeight: '95vh',
      maxWidth: '95vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((file: File | undefined) => {
      if (!file) return;
      this.onPhotoReady(file);
    });
  }
  onPhotoReady(file: File): void {
    if (this.photoPreviewUrl) {
      URL.revokeObjectURL(this.photoPreviewUrl);
    }
    this.photoFile = file;
    this.photoPreviewUrl = URL.createObjectURL(file);
    this.onSaveChanges();
  }

  onConfirm(): void {
    this.showConfirm = false;
    const merchantId = this.route.snapshot.paramMap.get('id');

    let service$: Observable<any>;

    if (this.return === 'merchant' && merchantId) {
      switch (this.pendingAction) {
        case 'reset-password':
          service$ = this.merchantAccountService.resetPassword(merchantId, this.account!.id);
          break;
        case 'reset-2fa':
          service$ = this.merchantAccountService.reset2FA(merchantId, this.account!.id);
          break;
        case 'reset-account':
          service$ = this.merchantAccountService.resetAccount(merchantId, this.account!.id);
          break;
        default:
          console.warn('Unknown security action:', this.pendingAction);
          return;
      }
    } else {
      switch (this.pendingAction) {
        case 'reset-password':
          service$ = this.accountService.resetPassword(this.account!.id);
          break;
        case 'reset-2fa':
          service$ = this.accountService.reset2FA(this.account!.id);
          break;
        case 'reset-account':
          service$ = this.accountService.resetAccount(this.account!.id);
          break;
        default:
          console.warn('Unknown security action:', this.pendingAction);
          return;
      }
    }

    this.loading = true;
    service$.pipe(finalize(() => (this.loading = false))).subscribe({
      next: () => {
        this.securityDropdownOpen = false;
      },
    });
  }

  onCancel(): void {
    this.showConfirm = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;

  get permissionResource() {
    return this.return === 'merchant' ? Resources.MerchantAccounts : Resources.Accounts;
  }
}
